/**
 * Whitelabel Configuration Composable
 * Provides Vue 3 composition API access to whitelabel configuration via inject pattern
 */

import { inject } from 'vue'
import type { WhitelabelConfig } from '../config/whitelabel'
import {
  WhitelabelConfigInjectionKey,
  WhitelabelInjectionError,
  type UseWhitelabelConfigReturn,
  type UseWhitelabelAvailabilityReturn,
  type UseWhitelabelFeaturesReturn,
  type UseWhitelabelBrandingReturn,
  isWhitelabelConfig
} from '../types/whitelabel'

// Legacy symbol for backward compatibility
export const WHITELABEL_CONFIG_KEY = WhitelabelConfigInjectionKey

/**
 * Composable for accessing whitelabel configuration in components
 * Provides error handling and fallback behavior - Subtask 5.4
 */
export function useWhitelabelConfig(): UseWhitelabelConfigReturn {
  try {
    const injectedConfig = inject(WhitelabelConfigInjectionKey)
    
    if (!injectedConfig) {
      console.warn('⚠️ Whitelabel configuration not provided. Using fallback configuration.')
      
      // Return fallback configuration with safe defaults
      return {
        config: createFallbackConfig(),
        isAvailable: false,
        error: new WhitelabelInjectionError('Configuration not provided via injection')
      }
    }
    
    // Validate injected configuration
    if (!isWhitelabelConfig(injectedConfig)) {
      console.error('❌ Invalid whitelabel configuration injected')
      return {
        config: createFallbackConfig(),
        isAvailable: false,
        error: new WhitelabelInjectionError('Invalid configuration format')
      }
    }
    
    return {
      config: injectedConfig,
      isAvailable: true
    }
  } catch (error) {
    console.error('❌ Error accessing whitelabel configuration:', error)
    
    return {
      config: createFallbackConfig(),
      isAvailable: false,
      error: error instanceof Error ? error : new Error('Unknown injection error')
    }
  }
}

/**
 * Create fallback configuration when injection fails
 * Ensures application continues to work with safe defaults
 */
function createFallbackConfig(): WhitelabelConfig {
  return {
    appName: 'Rewards App',
    primaryColor: '#3880ff',
    secondaryColor: '#32db64',
    logoUrl: 'logo.png',
    faviconUrl: 'favicon.ico',
    features: {
      showLeaderboard: true,
      allowUserSwitching: true,
      allowLanguageSwitching: true,
      showProfilePage: true,
      enableNotifications: true
    }
  }
}

/**
 * Hook for checking if whitelabel configuration is available
 * Useful for conditional rendering based on configuration availability
 */
export function useWhitelabelAvailability(): UseWhitelabelAvailabilityReturn {
  const { config, isAvailable } = useWhitelabelConfig()
  
  return {
    isConfigured: isAvailable,
    hasCustomBranding: isAvailable && (
      config.appName !== 'Rewards App' ||
      config.primaryColor !== '#3880ff' ||
      config.logoUrl !== 'logo.png'
    )
  }
}

/**
 * Hook for accessing specific whitelabel features
 * Provides type-safe access to feature flags
 */
export function useWhitelabelFeatures(): UseWhitelabelFeaturesReturn {
  const { config } = useWhitelabelConfig()
  
  return {
    features: config.features,
    isFeatureEnabled: (feature: keyof WhitelabelConfig['features']): boolean => {
      return config.features[feature] ?? false
    }
  }
}

/**
 * Hook for accessing whitelabel branding
 * Provides convenient access to branding-related configuration
 */
export function useWhitelabelBranding(): UseWhitelabelBrandingReturn {
  const { config, isAvailable } = useWhitelabelConfig()
  
  return {
    appName: config.appName,
    primaryColor: config.primaryColor,
    secondaryColor: config.secondaryColor,
    logoUrl: config.logoUrl || '',
    faviconUrl: config.faviconUrl || '',
    hasCustomBranding: isAvailable
  }
}

/**
 * Utility hook for error handling and debugging
 * Provides access to injection errors and configuration status
 */
export function useWhitelabelDebug() {
  const result = useWhitelabelConfig()
  
  return {
    hasError: !!result.error,
    error: result.error,
    isConfigured: result.isAvailable,
    configSource: result.isAvailable ? 'injected' : 'fallback',
    debugInfo: {
      injectionKey: WhitelabelConfigInjectionKey.toString(),
      configAvailable: result.isAvailable,
      errorMessage: result.error?.message,
      configPreview: {
        appName: result.config.appName,
        primaryColor: result.config.primaryColor,
        featureCount: Object.keys(result.config.features).length
      }
    }
  }
}